/**
 * Help text builder — generates the inline help content shown by /help.
 */

import type { SlashCommandMetadata } from '@mastra/code-sdk/utils/slash-command-loader';

export interface HelpTextOptions {
  /** Number of available controller modes (mode commands shown when > 1) */
  modes: number;
  /** User-defined custom slash commands */
  customSlashCommands: SlashCommandMetadata[];
  /** Active direct shell passthrough mode label */
  shellModeLabel?: string;
}

interface HelpEntry {
  key: string;
  description: string;
}

// =============================================================================
// Data
// =============================================================================

function getCommands(modes: number): HelpEntry[] {
  const cmds: HelpEntry[] = [
    { key: '/new', description: 'Start a new thread' },
    { key: '/threads', description: 'Switch between threads' },
    { key: '/thread', description: 'Show current thread info' },
    { key: '/thread:tag-dir', description: 'Tag thread with current directory' },
    { key: '/name', description: 'Rename current thread' },
    { key: '/resource', description: 'Show/switch resource ID' },
    { key: '/skills', description: 'List available skills' },
    { key: '/skill/<name>', description: 'Activate a skill' },
    { key: '/models', description: 'Switch model pack' },
    { key: '/custom-providers', description: 'Manage custom providers and models' },
    { key: '/subagents', description: 'Configure subagent models' },
    { key: '/permissions', description: 'Tool approval permissions' },
    { key: '/settings', description: 'Notifications, YOLO, thinking' },
    { key: '/memory', description: 'Configure Observational Memory (/om alias)' },
    { key: '/review', description: 'Review a GitHub pull request' },
    { key: '/report-issue', description: 'Open or browse mastracode issues' },
    { key: '/cost', description: 'Token usage and costs' },
    {
      key: '/prune',
      description: 'Prune old storage data — closes the TUI ("vacuum" reclaims disk, "keep-memory" keeps chats)',
    },
    { key: '/diff', description: 'Modified files or git diff' },
    { key: '/sandbox', description: 'Manage sandbox allowed paths' },
    { key: '/hooks', description: 'Show/reload configured hooks' },
    { key: '/mcp', description: 'Show/reload MCP connections' },
    { key: '/login', description: 'Login with OAuth provider' },
    { key: '/logout', description: 'Logout from OAuth provider' },
    { key: '/setup', description: 'Run the setup wizard' },
    { key: '/browser', description: 'Configure browser automation' },
    { key: '/api-keys', description: 'Manage provider API keys' },
    { key: '/plugins', description: 'Manage Mastra Code plugins' },
    { key: '/theme', description: 'Switch color theme (auto/dark/light)' },
    { key: '/update', description: 'Check for and install updates' },
    { key: '/observability', description: 'Configure cloud observability' },
    { key: '/github', description: 'Subscribe/sync GitHub PR signals' },
    { key: '/goal', description: 'Set/manage persistent goal (Ralph loop)' },
    { key: '/goal judge', description: 'Set the goal judge model and max attempts' },
  ];

  if (modes > 1) {
    cmds.push({ key: '/mode', description: 'Switch or list modes' });
  }

  cmds.push({ key: '/exit', description: 'Exit' }, { key: '/help', description: 'Show this help' });

  return cmds;
}

function getShortcuts(modes: number): HelpEntry[] {
  const shortcuts: HelpEntry[] = [
    { key: 'Ctrl+C', description: 'Interrupt / clear input' },
    { key: 'Ctrl+C×2', description: 'Exit (double-tap)' },
    { key: 'Ctrl+D', description: 'Exit (when editor empty)' },
    { key: 'Enter', description: 'Send message' },
    { key: 'Ctrl+F', description: 'Queue follow-up' },
    { key: 'Ctrl+T', description: 'Toggle thinking blocks' },
    { key: 'Ctrl+E', description: 'Expand/collapse tool outputs' },
    { key: 'Ctrl+Y', description: 'Toggle YOLO mode' },
    { key: 'Ctrl+Z', description: 'Suspend process (fg to resume)' },
    { key: 'Alt+Z', description: 'Undo last clear' },
  ];

  if (modes > 1) {
    shortcuts.push({ key: '⇧+Tab', description: 'Cycle agent modes' });
  }

  shortcuts.push({ key: '/', description: 'Commands' }, { key: '!', description: 'Shell' });

  return shortcuts;
}

// =============================================================================
// Rendering
// =============================================================================

function renderSection(title: string, entries: HelpEntry[]): string {
  const maxKeyLen = Math.max(...entries.map(e => e.key.length));
  const lines = entries.map(e => `  ${e.key.padEnd(maxKeyLen + 2)}${e.description}`).join('\n');
  return `${title}\n${lines}`;
}

/**
 * Build the full help text as a plain string for inline display via showInfo().
 */
export function buildHelpText(options: HelpTextOptions): string {
  const sections: string[] = [];

  sections.push(renderSection('Commands', getCommands(options.modes)));

  if (options.customSlashCommands.length > 0) {
    const customEntries = options.customSlashCommands.map(cmd => ({
      key: `//${cmd.name}`,
      description: cmd.description || 'No description',
    }));
    sections.push(renderSection('Custom Commands', customEntries));
  }

  sections.push(
    renderSection('Shell', [
      {
        key: '!<cmd>',
        description: `Run a direct shell command (${options.shellModeLabel ?? 'default shell'})`,
      },
    ]),
  );

  sections.push(renderSection('Keyboard Shortcuts', getShortcuts(options.modes)));

  return sections.join('\n\n');
}
